import { useCallback, useMemo, useState } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { PriceObject } from "@/lib/gql/__generated__/graphql";

export type TicketListItemType = {
	id: string;
	name: string;
	validMonth: number;
	price: PriceObject;
	isPersonal?: boolean;
};

type Props = {
	/**
	 * 初期描画時に選択しておくチケットのID
	 */
	defaultTicketID?: string;

	/**
	 * 描画したいチケットのリスト
	 */
	ticketList: TicketListItemType[];

	/**
	 * 選択中のチケットのアクセントカラー
	 */
	accentColor?: "blue" | "yellow";
};

export const useTicketList = ({
	defaultTicketID,
	ticketList,
	accentColor = "blue", //
}: Props) => {
	// price を降順に並び替える
	const filteredTicketList = useMemo(() => ticketList.sort((a, b) => a.price.amount - b.price.amount), [ticketList]);

	const [selectedTicketID, setSelectedTicketID] = useState<string | null>(null);

	const handleChangeSelectedTicketID = useCallback((ticketID: string) => {
		setSelectedTicketID(ticketID);
	}, []);

	const handleClickTicket = useCallback((ticketID: string) => {
		setSelectedTicketID(ticketID);
	}, []);

	// 最もお得なチケット（１ヶ月毎の料金にしたときに1番安いチケット）のインデックスを取得
	const bestDealTicketIndex = useMemo(
		() =>
			filteredTicketList.reduce((acc, ticket, idx) => {
				if (!ticket) return acc;

				const pricePerMonth = ticket.price.amount / ticket.validMonth;

				if (pricePerMonth < filteredTicketList[acc].price.amount / filteredTicketList[acc].validMonth) {
					return idx;
				}

				return acc;
			}, 0),
		[filteredTicketList],
	);

	const borderColor = accentColor === "blue" ? "border-blue" : "border-yellow";
	const bgColor = accentColor === "blue" ? "bg-blue-400" : "bg-yellow-400";

	const ticketList_Component = () => {
		return (
			<>
				<div className="grid grid-cols-1 gap-4 tablet:grid-cols-3 tablet:gap-6">
					{filteredTicketList ? (
						filteredTicketList.map((ticket, idx) => {
							if (!ticket) return null;

							const ticketID = ticket.id;
							const isSelected = selectedTicketID
								? selectedTicketID === ticketID // チケットが選択されている場合：ユーザーが選択したチケットID と一致するかどうか
								: defaultTicketID === ticketID; // チケットが選択されていない場合：初期値として設定されているチケットID と一致するかどうか
							const isBestDeal = bestDealTicketIndex === idx;

							return (
								<button
									key={ticket.id}
									className={cn(
										"relative flex h-auto min-h-[120px] flex-col items-center justify-center gap-y-2 rounded border-[6px] border-zinc-300 py-4 tablet:h-[200px] tablet:max-w-[240px] tablet:py-0",
										isSelected && cn(borderColor),
									)}
									onClick={() => handleClickTicket(ticket.id)}
								>
									{isSelected && (
										<div
											className={cn(
												"absolute -top-5 z-10 rounded border-[4px] px-3 text-lg tracking-widest text-white",
												borderColor,
												bgColor,
											)}
										>
											<p>選択中</p>
										</div>
									)}

									<div className="absolute h-full w-full overflow-hidden">
										{isBestDeal && (
											<div
												className={cn(
													"absolute -right-10 top-6 w-36 rotate-45 bg-green-600 py-1 text-[15px] tracking-widest text-white",
												)}
											>
												<p>最もお得！</p>
											</div>
										)}
									</div>

									<p className="line-clamp-2">{ticket.name}</p>
									<p className="text-2xl font-bold">
										¥ {Math.floor(ticket.price.amount / ticket.validMonth).toLocaleString()} / 月
									</p>
									<p>¥ {ticket.price.amount.toLocaleString()} - </p>
								</button>
							);
						})
					) : (
						<div className="col-span-3">
							<p>購入できるチケットがありません</p>
						</div>
					)}
				</div>
			</>
		);
	};

	return {
		selectedTicketID,
		ticketList_Component,

		handleChangeSelectedTicketID,
	};
};
